import { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';

interface Suggestion {
  field: string;
  currentValue: number;
  suggestedValue: number;
  reasoning: string;
}

interface AttachmentMeta {
  file_name: string;
  file_size: number;
  mime_type: string;
  storage_path: string;
  sendable: boolean;
}

interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  suggestions?: Suggestion[];
  applied?: boolean;
  starred?: boolean;
  attachments?: AttachmentMeta[];
}

interface ChatSession {
  id: string;
  title: string;
  updated_at: string;
}

interface PendingFile {
  file: File;
  uploading: boolean;
}

interface DealAdvisorChatProps {
  propertyId: string;
  dealContext: Record<string, number | string>;
  onApplySuggestion: (field: string, value: number) => void;
  thresholds: { irr: number; cap_rate: number | null; cash_on_cash: number; equity_multiple: number; dscr: number };
  strategy: string;
}

const FIELD_LABELS: Record<string, string> = {
  purchase_price: 'Purchase Price',
  potential_gross_income: 'Gross Income (PGI)',
  vacancy_rate: 'Vacancy Rate',
  property_taxes: 'Property Taxes',
  insurance: 'Insurance',
  utilities: 'Utilities',
  repairs_maintenance: 'Repairs & Maintenance',
  reserves_capex: 'Reserves / CapEx',
  other_expenses: 'Other Expenses',
  management_fee_percent: 'Management Fee %',
  ltv_percent: 'LTV %',
  interest_rate: 'Interest Rate',
  amortization_years: 'Amortization',
  closing_costs_percent: 'Closing Costs %',
  exit_cap_rate: 'Exit Cap Rate',
  selling_costs_percent: 'Selling Costs %',
  income_growth_rate: 'Income Growth',
  expense_growth_rate: 'Expense Growth',
  holding_period: 'Holding Period',
};

const PERCENT_FIELDS = new Set([
  'vacancy_rate', 'management_fee_percent', 'ltv_percent', 'interest_rate',
  'closing_costs_percent', 'exit_cap_rate', 'selling_costs_percent',
  'income_growth_rate', 'expense_growth_rate',
]);

const YEAR_FIELDS = new Set(['amortization_years', 'holding_period']);

const MAX_FILES = 3;
const MAX_SIZE = 25 * 1024 * 1024;

const ALLOWED_TYPES = new Set([
  'application/pdf', 'image/jpeg', 'image/png', 'image/webp',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword', 'text/csv', 'text/plain',
]);

const SENDABLE_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/webp']);

function formatFieldValue(field: string, value: number): string {
  if (PERCENT_FIELDS.has(field)) return `${value.toFixed(2)}%`;
  if (YEAR_FIELDS.has(field)) return `${value} yrs`;
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function percentChange(current: number, suggested: number): string {
  if (current === 0) return '';
  const pct = ((suggested - current) / Math.abs(current)) * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DealAdvisorChat({
  propertyId,
  dealContext,
  onApplySuggestion,
  thresholds,
  strategy,
}: DealAdvisorChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [appliedSet, setAppliedSet] = useState<Set<string>>(new Set());
  const [starredSet, setStarredSet] = useState<Set<string>>(new Set());
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  // Load most recent chat on mount
  useEffect(() => {
    if (historyLoaded) return;
    setHistoryLoaded(true);

    (async () => {
      try {
        const res = await api.dealAdvisorHistory(propertyId);
        const chats = res.chats || [];
        if (chats.length === 0) return;

        setChatSessions(chats.map((c: any) => ({ id: c.id, title: c.title, updated_at: c.updated_at })));

        // Load the most recent chat
        const latest = chats[0];
        const msgs = (latest.ai_deal_messages || [])
          .sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

        if (msgs.length > 0) {
          setChatId(latest.id);
          const restoredApplied = new Set<string>();
          const restoredStarred = new Set<string>();

          const restored: ChatMessage[] = msgs.map((m: any) => {
            const suggestions = m.suggestion_data?.suggestions || [];
            if (m.applied && m.id) {
              suggestions.forEach((s: any) => restoredApplied.add(`${m.id}-${s.field}`));
            }
            if (m.starred && m.id) {
              restoredStarred.add(m.id);
            }
            return {
              id: m.id,
              role: m.role,
              content: m.content,
              suggestions,
              applied: m.applied,
              starred: m.starred,
              attachments: m.attachments || undefined,
            };
          });

          setMessages(restored);
          setAppliedSet(restoredApplied);
          setStarredSet(restoredStarred);
        }
      } catch (err) {
        console.error('[DealAdvisor] Failed to load history:', err);
      }
    })();
  }, [propertyId, historyLoaded]);

  const strategyLabel = strategy === 'core' ? 'Core' : strategy === 'value_add' ? 'Value-Add' : 'Opportunistic';

  const quickActions = [
    { label: 'Hit IRR target', prompt: `What purchase price do I need to hit the ${thresholds.irr}% IRR target?` },
    { label: 'Improve DSCR', prompt: 'How can I get the DSCR above 1.25x?' },
    { label: 'Maximize CoC', prompt: 'What changes would maximize my cash-on-cash return?' },
    { label: 'Price -10%', prompt: 'What would my returns look like if I negotiate 10% off the asking price?' },
    { label: 'Raise rent', prompt: 'What rent per SF do I need to make this deal work?' },
  ];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const currentCount = pendingFiles.length;
    const newFiles: PendingFile[] = [];

    for (let i = 0; i < files.length && currentCount + newFiles.length < MAX_FILES; i++) {
      const file = files[i];
      if (!ALLOWED_TYPES.has(file.type)) continue;
      if (file.size > MAX_SIZE) continue;
      newFiles.push({ file, uploading: false });
    }

    setPendingFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadPendingFiles = async (): Promise<AttachmentMeta[]> => {
    if (pendingFiles.length === 0) return [];

    const fileMeta = pendingFiles.map((pf) => ({
      file_name: pf.file.name,
      file_size: pf.file.size,
      mime_type: pf.file.type,
    }));

    const urlRes = await api.dealAdvisorUploadUrl({ files: fileMeta });
    const uploads = urlRes.uploads;

    const attachments: AttachmentMeta[] = [];

    for (let i = 0; i < uploads.length; i++) {
      const upload = uploads[i];
      const pf = pendingFiles[i];

      try {
        setPendingFiles((prev) => prev.map((f, idx) => idx === i ? { ...f, uploading: true } : f));

        const uploadRes = await fetch(upload.signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': pf.file.type },
          body: pf.file,
        });

        if (!uploadRes.ok) throw new Error('Upload failed');

        attachments.push({
          file_name: pf.file.name,
          file_size: pf.file.size,
          mime_type: pf.file.type,
          storage_path: upload.storagePath,
          sendable: SENDABLE_TYPES.has(pf.file.type),
        });
      } catch (err) {
        console.error(`Failed to upload ${pf.file.name}:`, err);
      }
    }

    return attachments;
  };

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    setInput('');
    setLoading(true);

    try {
      const uploadedAttachments = await uploadPendingFiles();

      const userMsg: ChatMessage = {
        role: 'user',
        content: text.trim(),
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
      };
      setMessages(prev => [...prev, userMsg]);
      setPendingFiles([]);

      const result = await api.dealAdvisorAsk({
        property_id: propertyId,
        message: text.trim(),
        chat_id: chatId,
        deal_context: dealContext,
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
      });

      if (!chatId && result.chat_id) {
        setChatId(result.chat_id);
      }

      const assistantMsg: ChatMessage = {
        id: result.message_id,
        role: 'assistant',
        content: result.message,
        suggestions: result.suggestions || [],
        applied: false,
        starred: false,
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        role: 'assistant',
        content: `Sorry, I couldn't process that request. ${err.message || 'Please try again.'}`,
        suggestions: [],
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = (msgId: string | undefined, suggestion: Suggestion) => {
    onApplySuggestion(suggestion.field, suggestion.suggestedValue);
    if (msgId) {
      setAppliedSet(prev => new Set(prev).add(`${msgId}-${suggestion.field}`));
      api.dealAdvisorApplySuggestion({ message_id: msgId }).catch(() => {});
    }
  };

  const handleToggleStar = (msgId: string | undefined) => {
    if (!msgId) return;
    const isStarred = starredSet.has(msgId);
    const newStarred = !isStarred;

    setStarredSet(prev => {
      const next = new Set(prev);
      if (newStarred) next.add(msgId);
      else next.delete(msgId);
      return next;
    });

    api.dealAdvisorStarMessage({ message_id: msgId, starred: newStarred }).catch(() => {
      // Revert on failure
      setStarredSet(prev => {
        const next = new Set(prev);
        if (isStarred) next.add(msgId);
        else next.delete(msgId);
        return next;
      });
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const startNewChat = () => {
    setChatId(null);
    setMessages([]);
    setAppliedSet(new Set());
    setStarredSet(new Set());
    setPendingFiles([]);
    setShowHistory(false);
  };

  const loadChat = async (session: ChatSession) => {
    try {
      const res = await api.dealAdvisorHistory(propertyId);
      const chat = (res.chats || []).find((c: any) => c.id === session.id);
      if (!chat) return;

      const msgs = (chat.ai_deal_messages || [])
        .sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      const restoredApplied = new Set<string>();
      const restoredStarred = new Set<string>();

      const restored: ChatMessage[] = msgs.map((m: any) => {
        const suggestions = m.suggestion_data?.suggestions || [];
        if (m.applied && m.id) {
          suggestions.forEach((s: any) => restoredApplied.add(`${m.id}-${s.field}`));
        }
        if (m.starred && m.id) {
          restoredStarred.add(m.id);
        }
        return {
          id: m.id, role: m.role, content: m.content, suggestions, applied: m.applied, starred: m.starred,
          attachments: m.attachments || undefined,
        };
      });

      setChatId(session.id);
      setMessages(restored);
      setAppliedSet(restoredApplied);
      setStarredSet(restoredStarred);
      setPendingFiles([]);
      setShowHistory(false);
    } catch (err) {
      console.error('[DealAdvisor] Failed to load chat:', err);
    }
  };

  const renderAttachmentBadges = (attachments?: AttachmentMeta[]) => {
    if (!attachments || attachments.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {attachments.map((att, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 rounded text-[10px] opacity-80">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            {att.file_name}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-info to-gray-800 rounded-xl p-5 border border-info/30">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-info-wash rounded-lg">
            <svg className="w-5 h-5 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-info">AI Deal Advisor</h3>
            <p className="text-xs text-info">
              Ask questions about this {strategyLabel} deal — get specific suggestions you can apply instantly
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { setShowHistory(!showHistory); if (!showHistory) api.dealAdvisorHistory(propertyId).then(r => setChatSessions((r.chats || []).map((c: any) => ({ id: c.id, title: c.title, updated_at: c.updated_at })))).catch(() => {}); }}
              className="p-1.5 rounded-lg text-info hover:bg-info-wash transition-colors"
              title="Chat History"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button
              onClick={startNewChat}
              className="p-1.5 rounded-lg text-info hover:bg-info-wash transition-colors"
              title="New Chat"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Chat history dropdown */}
        {showHistory && chatSessions.length > 0 && (
          <div className="mt-3 bg-white rounded-lg border border-info/30 max-h-40 overflow-y-auto">
            {chatSessions.map(s => (
              <button
                key={s.id}
                onClick={() => loadChat(s)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-info-wash transition-colors border-b border-gray-100 last:border-0 ${s.id === chatId ? 'bg-info-wash text-info font-medium' : 'text-gray-700'}`}
              >
                <div className="truncate">{s.title || 'Untitled chat'}</div>
                <div className="text-xs text-gray-400">
                  {new Date(s.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Quick Action Chips */}
        <div className="flex flex-wrap gap-2 mt-3">
          {quickActions.map(action => (
            <button
              key={action.label}
              onClick={() => sendMessage(action.prompt)}
              disabled={loading}
              className="px-3 py-1.5 text-xs font-medium bg-white text-info border border-info/30 rounded-full hover:bg-info-wash hover:border-info/30 transition-colors disabled:opacity-50"
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {/* Star legend */}
      {starredSet.size > 0 && (
        <div className="flex items-center gap-2 px-1 text-xs text-warning">
          <svg className="w-3.5 h-3.5 fill-warning" viewBox="0 0 24 24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          {starredSet.size} starred insight{starredSet.size !== 1 ? 's' : ''} will appear in the PDF export
        </div>
      )}

      {/* Chat Messages */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="h-[400px] overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="font-medium">No messages yet</p>
                <p className="text-sm mt-1">Try a quick action above or type your own question</p>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                {/* Message bubble */}
                <div className={`rounded-2xl px-4 py-3 relative group ${
                  msg.role === 'user'
                    ? 'bg-info text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === 'user' && renderAttachmentBadges(msg.attachments)}

                  {/* Star button for assistant messages */}
                  {msg.role === 'assistant' && msg.id && (
                    <button
                      onClick={() => handleToggleStar(msg.id)}
                      className={`absolute -top-2 -right-2 p-1 rounded-full border shadow-sm transition-all ${
                        starredSet.has(msg.id!)
                          ? 'bg-warning-wash border-warning/30 text-warning'
                          : 'bg-white border-gray-200 text-gray-300 opacity-0 group-hover:opacity-100'
                      }`}
                      title={starredSet.has(msg.id!) ? 'Remove from PDF' : 'Star for PDF export'}
                    >
                      <svg className={`w-3.5 h-3.5 ${starredSet.has(msg.id!) ? 'fill-warning' : 'fill-none'}`} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Suggestion cards */}
                {msg.role === 'assistant' && msg.suggestions && msg.suggestions.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {msg.suggestions.map((suggestion, j) => {
                      const key = `${msg.id}-${suggestion.field}`;
                      const isApplied = appliedSet.has(key);
                      return (
                        <div
                          key={j}
                          className={`rounded-xl border p-3 transition-colors ${
                            isApplied
                              ? 'bg-success-wash border-success/30'
                              : 'bg-white border-gray-200 hover:border-info/30'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                              {FIELD_LABELS[suggestion.field] || suggestion.field}
                            </span>
                            {isApplied ? (
                              <span className="flex items-center gap-1 text-xs text-success font-medium">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Applied
                              </span>
                            ) : (
                              <button
                                onClick={() => handleApply(msg.id, suggestion)}
                                className="px-3 py-1 text-xs font-medium bg-info text-white rounded-lg hover:bg-info/85 transition-colors"
                              >
                                Apply
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 line-through">
                              {formatFieldValue(suggestion.field, suggestion.currentValue)}
                            </span>
                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            <span className="font-semibold text-info">
                              {formatFieldValue(suggestion.field, suggestion.suggestedValue)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {percentChange(suggestion.currentValue, suggestion.suggestedValue)}
                            </span>
                          </div>
                          {suggestion.reasoning && (
                            <p className="text-xs text-gray-500 mt-1">{suggestion.reasoning}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Pending Files */}
        {pendingFiles.length > 0 && (
          <div className="px-3 py-2 border-t bg-gray-50 flex flex-wrap gap-1.5">
            {pendingFiles.map((pf, i) => (
              <span key={i} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 bg-white rounded-lg text-xs text-gray-600 border">
                <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                <span className="truncate max-w-[140px]">{pf.file.name}</span>
                <span className="text-gray-400">({formatFileSize(pf.file.size)})</span>
                {pf.uploading ? (
                  <div className="w-3 h-3 border border-info border-t-transparent rounded-full animate-spin" />
                ) : (
                  <button onClick={() => removePendingFile(i)} className="p-0.5 rounded hover:bg-gray-100">
                    <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="border-t p-3 bg-gray-50">
          <div className="flex items-end gap-2">
            {/* Paperclip button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || pendingFiles.length >= MAX_FILES}
              className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 transition-colors flex-shrink-0"
              title="Attach file (PDF, image, Excel, Word, CSV)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.docx,.doc,.csv,.txt"
              onChange={handleFileSelect}
            />
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this deal..."
              rows={1}
              className="flex-1 resize-none rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-info focus:border-transparent"
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              className="p-2.5 bg-info text-white rounded-xl hover:bg-info/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
